import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import 'features/datasets/components/DatasetGrid.css';
import 'features/datasets/components/DatasetWorkspace.css';
import { Filter as FilterIcon, Search, X as XIcon } from 'lucide-react';
import { listDatasetItems, replaceRows } from 'features/datasets/api/datasetsApi';
import { useAppDialog } from 'shared/context/AppDialogContext';
import BulkBar from 'shared/components/tables/BulkBar';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import DerivedColumnModal from 'features/datasets/components/DerivedColumnModal';
import ImportDialog from 'features/datasets/components/ImportDialog';
import DatasetFilter, { newFilter } from 'features/datasets/components/DatasetFilter';
import { evaluateDerivedColumn, normalizeDerivedColumns } from 'features/datasets/utils/derivedColumnEvaluator';
import {
  parseImportFile, buildImportedRows, ImportProblem, RESERVED,
} from 'features/datasets/utils/importParsers';
import { applyRowFilters, isActive, SEARCH_FIELD } from 'features/datasets/utils/rowFilter';
import { toCsv } from 'features/datasets/utils/csv';
import { normalizeTags } from 'features/datasets/utils/tags';
import {
  normalizeComments, originKind, originLabel, originTitle,
} from 'features/datasets/utils/collaboration';
import {
  outputText, unitOf, evaluationLines, evaluationsText, inputText, inputLines, textLines, isVerdictValue, verdictText, linksOf, LINK_KEYS,
} from 'features/datasets/utils/units';

let _uid = 0;
const newId = () => `r${Date.now().toString(36)}_${_uid++}`;
const PAGE_SIZE = 50;
const metaToStr = (m) => (m == null || m === '' ? '' : (typeof m === 'string' ? m : JSON.stringify(m)));
const displayVal = (v) => (v == null ? '' : (typeof v === 'string' ? v : JSON.stringify(v)));

/** The text columns' starting widths; rowHeightFor estimates line counts against them. */
const TEXT_COL_WIDTH = { input: 360, output: 240, expected: 240, evaluations: 320 };
const ROW_LINE_PX = 17;
const ROW_MIN_PX = 32;
const ROW_MAX_PX = 340;
const HEADER_ROW_HEIGHT = 32;

/** List-and-plus mark for a dataset that has no rows yet. */
function DatasetEmptyArt() {
  return (
    <svg className="ds-dataset-empty-art" viewBox="0 0 144 96" fill="none" aria-hidden="true">
      <rect className="ds-empty-card" x="0.5" y="1" width="112" height="70" rx="7" />
      <rect className="ds-empty-mark" x="14" y="14" width="8" height="8" rx="1.5" />
      <rect className="ds-empty-bar" x="28" y="15.5" width="62" height="5" rx="2.5" />
      <rect className="ds-empty-mark" x="14" y="30" width="8" height="8" rx="1.5" />
      <rect className="ds-empty-bar" x="28" y="31.5" width="62" height="5" rx="2.5" />
      <rect className="ds-empty-mark" x="14" y="46" width="8" height="8" rx="1.5" />
      <rect className="ds-empty-bar" x="28" y="47.5" width="72" height="5" rx="2.5" />
      <rect className="ds-empty-plus-box" x="88" y="40" width="52" height="52" rx="5" />
      <path className="ds-empty-plus" d="M104 66h20M114 56v20" />
    </svg>
  );
}

function rowInputText(row, columns) {
  const vars = {};
  for (const c of columns) if (row[c] != null && row[c] !== '') vars[c] = coerceVal(row[c]);
  return inputText(row.__messages, vars, row.__query);
}
function rowOutputText(row) {
  const text = isVerdictValue(row.__output) ? verdictText(row.__output) : outputText(row.__output);
  return typeof text === 'string' ? text.trim() : (text == null ? '' : JSON.stringify(text));
}
function rowExpectedText(row) {
  const text = isVerdictValue(row.__expectedRaw) ? verdictText(row.__expectedRaw) : row.__expected;
  return typeof text === 'string' ? text.trim() : (text == null ? '' : JSON.stringify(text));
}

/** A row is as tall as its longest text column needs, between 32px and 340px (then the cell scrolls). */
function rowHeightFor(row, columns, shown) {
  const lines = Math.max(
    textLines(rowInputText(row, columns), TEXT_COL_WIDTH.input),
    shown.output ? textLines(rowOutputText(row), TEXT_COL_WIDTH.output) : 1,
    shown.expected ? textLines(rowExpectedText(row), TEXT_COL_WIDTH.expected) : 1,
    shown.evaluations ? textLines(evaluationsText(row.__evaluations), TEXT_COL_WIDTH.evaluations) : 1,
  );
  return Math.min(ROW_MAX_PX, Math.max(ROW_MIN_PX, 12 + ROW_LINE_PX * lines));
}

function formatJsonCell(val) {
  if (val == null || val === '') return null;
  const s = typeof val === 'string' ? val.trim() : JSON.stringify(val);
  if (!s) return null;
  return s.length > 96 ? `${s.slice(0, 93)}…` : s;
}

function formatCreated(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return '—';
  }
}

// Reserved column names that are NOT input variables — they're the item's
// first-class expected-output / metadata sections. If an older flat dataset put
// them among its columns, we migrate them out of the input set on load.

function originFromItem(it) {
  const ref = it.source_trace_ref && typeof it.source_trace_ref === 'object' ? it.source_trace_ref : null;
  const meta = it.metadata && typeof it.metadata === 'object' ? it.metadata : {};
  if (ref?.span_id || ref?.experiment_id || ref?.source_kind || ref?.import_kind) return { ...ref };
  if (ref) return { ...ref };
  if (meta.source_span_id) return { span_id: meta.source_span_id, run_id: meta.run_id || null };
  if (meta.source_experiment_id) return { experiment_id: meta.source_experiment_id };
  return null;
}

// Coerce a flat cell string back to a typed value for storage. We only promote
// UNAMBIGUOUS JSON — objects `{…}`, arrays `[…]`, and the bare literals
// true/false/null. Everything else (including plain numbers like ZIP "02139" or
// phone numbers) stays a string; the prompt's declared variable TYPE does any
// number/boolean coercion at compile time, so this can't corrupt scalar data.
const coerceVal = (s) => {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if (t === '') return '';
  const c = t[0];
  if (c === '{' || c === '[') { try { return JSON.parse(t); } catch { return s; } }
  if (t === 'true') return true;
  if (t === 'false') return false;
  if (t === 'null') return null;
  return s;
};

function buildRowSnapshot(row, columns) {
  const input = {};
  for (const c of columns) {
    if (row[c] != null) input[c] = coerceVal(row[c]);
  }
  let metadata = {};
  const mstr = row.__metadata != null ? String(row.__metadata).trim() : '';
  if (mstr) {
    try {
      const parsed = JSON.parse(mstr);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) metadata = parsed;
      else metadata = { value: parsed };
    } catch { metadata = { note: mstr }; }
  }
  return {
    id: row.__id,
    itemId: row.__itemId || null,
    input,
    expected: row.__expected ?? '',
    expectedRaw: row.__expectedRaw ?? null,
    metadata,
    origin: row.__origin || null,
    assignedTo: row.__assigned != null && row.__assigned !== '' ? Number(row.__assigned) : null,
    comments: normalizeComments(row.__comments),
    tags: normalizeTags(row.__tags),
    createdAt: row.__created || null,
    position: row.__position ?? null,
    // Phase-1 columns, read-only in the grid: shown in the drawer.
    output: row.__output ?? null,
    messages: row.__messages ?? null,
    query: row.__query ?? null,
    evaluations: Array.isArray(row.__evaluations) ? row.__evaluations : [],
    unit: row.__unit || { kind: 'manual' },
    expired: Boolean(row.__expired),
  };
}

/**
 * What goes back to the server for Expected. Untouched, the original value
 * (a rubric object stays an object); edited, the text, promoted to JSON only
 * when it unambiguously is JSON.
 */
function expectedForSave(text, raw) {
  if (text === '' || text == null) return null;
  if (raw != null && typeof raw !== 'string' && JSON.stringify(raw) === text) return raw;
  if (typeof raw === 'string' && raw === text) return raw;
  return coerceVal(text);
}

const HeaderLabel = ({ name, onClick, title }) => (
  <div className="ds-col-icon font-semibold" title={title}>
    {onClick ? (
      <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} onMouseDown={(e) => e.stopPropagation()}
        className="truncate font-semibold uppercase tracking-[0.05em] hover:text-[color:var(--text-primary)]">{name}</button>
    ) : <span className="truncate">{name}</span>}
  </div>
);

// Map react-data-grid's CSS custom properties onto the app theme vars so the
// grid follows dark/light automatically, with visible cell lines.
const gridThemeVars = {
  '--rdg-color': 'var(--text-primary)',
  '--rdg-background-color': 'var(--surface-base)',
  '--rdg-header-background-color': 'var(--surface-base)',
  '--rdg-row-hover-background-color': 'var(--surface-hover)',
  '--rdg-row-selected-background-color': 'var(--surface-hover)',
  '--rdg-border-color': 'var(--border-default)',
  '--rdg-summary-border-color': 'var(--border-default)',
  '--rdg-selection-color': 'var(--border-strong)',
  '--rdg-font-size': '12px',
  blockSize: '100%',
};

/** Columns the person can hide. The row number and tick boxes and the input columns are always there. */
const OPTIONAL_COLUMNS = [
  { key: '__links', label: 'Links' },
  { key: '__output', label: 'Output' },
  { key: '__expected', label: 'Expected' },
  { key: '__evaluations', label: 'Evaluations' },
  { key: '__metadata', label: 'Metadata' },
  { key: '__created', label: 'Created' },
  { key: '__updated', label: 'Updated' },
];

const linkBtn = 'max-w-full truncate rounded border border-[color:var(--border-default)] px-1.5 py-px text-[11px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover';

/** The Links cell: one chip per thing the row is about (eval, run, span, conversation), each opens it. */
function LinkChips({ links, expired, onOpen }) {
  if (!links.length) return null;
  return (
    <span className="flex h-full flex-wrap content-start items-start gap-1 overflow-hidden px-1.5 py-1">
      {links.map((l) => (expired && l.kind !== 'eval' ? (
        <span key={l.kind} className="truncate px-1 text-[11px] text-[color:var(--text-muted)]" title={`${l.title}: gone from retention; the row keeps its copy`}>
          {l.label} <span className="ds-expired-badge">expired</span>
        </span>
      ) : (
        <button key={l.kind} type="button" title={l.title} className={linkBtn}
          onClick={(e) => { e.stopPropagation(); onOpen?.(l.open); }}>{l.label}</button>
      )))}
    </span>
  );
}

/** The Metadata cell: key: value lines; an id the row links to is a link. */
function MetadataCell({ text, onOpen }) {
  let obj = null;
  if (text) { try { obj = JSON.parse(text); } catch { obj = null; } }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return <span className="ds-cell-json" title={text || undefined}>{formatJsonCell(text) || <span className="text-[color:var(--text-disabled)]">-</span>}</span>;
  }
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
  const open = (k, v) => (k === 'eval_run_id' && obj.lineage_id
    ? { kind: 'verdict', eval_run_id: v, lineage_id: obj.lineage_id }
    : LINK_KEYS[k] ? LINK_KEYS[k](v) : null);
  return (
    <span className="ds-text-cell">
      {entries.map(([k, v]) => {
        const target = typeof v === 'string' ? open(k, v) : null;
        return (
          <span key={k} className="ds-text-line">
            <span className="ds-text-key">{k}</span>{': '}
            {target ? (
              <button type="button" className="text-[color:var(--trodo-primary)] underline-offset-2 hover:underline" title={`Open ${v}`}
                onClick={(e) => { e.stopPropagation(); onOpen?.(target); }}>{v}</button>
            ) : <span className="ds-text-body">{typeof v === 'string' ? v : JSON.stringify(v)}</span>}
          </span>
        );
      })}
    </span>
  );
}

/** One chip per verdict: name, pass/fail, the thumbs state; the reason code on hover. */
/**
 * The Evaluations cell: every verdict as text, the final decision first, then
 * each step's decision on the way there, then the judge's words. The export
 * carries the same `evaluations` array, so what a reader sees is what ships.
 */
function EvaluationChips({ evaluations, onOpen }) {
  const list = Array.isArray(evaluations) ? evaluations : [];
  if (!list.length) return <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
  return (
    <span className="ds-text-cell">
      {list.map((ev, i) => {
        const { head, lines, outcome, title } = evaluationLines(ev);
        const tone = outcome === 'pass' ? 'ds-eval-pass' : outcome === 'fail' ? 'ds-eval-fail' : 'ds-eval-other';
        return (
          <span key={ev.eval_run_id || i} className="ds-text-line ds-eval-block" title={title}>
            {ev.lineage_id && ev.eval_run_id && onOpen ? (
              <button type="button" title="Open this verdict: every step and why" className={`ds-text-line ds-eval-head ${tone} text-left underline-offset-2 hover:underline`}
                onClick={(e) => { e.stopPropagation(); onOpen({ kind: 'verdict', eval_run_id: ev.eval_run_id, lineage_id: ev.lineage_id }); }}>
                <span className="ds-eval-dot" aria-hidden="true" />{head}
              </button>
            ) : <span className={`ds-text-line ds-eval-head ${tone}`}><span className="ds-eval-dot" aria-hidden="true" />{head}</span>}
            {lines.map((l, j) => <span key={j} className="ds-text-line ds-eval-step">{l}</span>)}
          </span>
        );
      })}
    </span>
  );
}

function ColumnsMenu({ hidden, onToggle }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className={activityToolbarButtonClass}>Columns</button>
      {open ? (
        <div role="menu" aria-label="Columns" className="absolute right-0 top-[calc(100%+4px)] z-30 w-44 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-1 shadow-token-lg">
          {OPTIONAL_COLUMNS.map((c) => (
            <label key={c.key} className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover">
              <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => onToggle(c.key)} />
              {c.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** A server item as a grid row. The server id is the row key, so a refetch stays put. */
function itemToRow(it, i = 0) {
  const vars = it.variables && typeof it.variables === 'object' ? it.variables : {};
  const input = {};
  let migExpected = null; let migMeta = null;
  for (const [k, v] of Object.entries(vars)) {
    if (k === 'expected_output' || k === 'expected') migExpected = v;
    else if (k === 'metadata') migMeta = v;
    else input[k] = displayVal(v);
  }
  const expected = it.expected_output != null ? it.expected_output : migExpected;
  const meta = it.metadata && Object.keys(it.metadata).length ? it.metadata : migMeta;
  return {
    __id: it.id ? String(it.id) : newId(),
    __itemId: it.id || null,
    __position: it.position ?? i,
    ...input,
    __expected: expected == null ? '' : (typeof expected === 'string' ? expected : JSON.stringify(expected)),
    __expectedRaw: expected == null ? null : expected,
    __metadata: metaToStr(meta),
    __origin: originFromItem(it),
    __assigned: it.assigned_to != null ? Number(it.assigned_to) : null,
    __comments: normalizeComments(it.comments),
    __tags: normalizeTags(it.tags),
    __created: it.created_at || null,
    __updated: it.labelled_at && (!it.updated_at || it.labelled_at > it.updated_at) ? it.labelled_at : (it.updated_at || null),
    __output: it.output ?? null,
    __messages: Array.isArray(it.messages) ? it.messages : null,
    // A row added as plain text keeps it as its query: shown as its input, kept on save.
    __query: typeof it.query === 'string' && it.query ? it.query : null,
    __evaluations: Array.isArray(it.evaluations) ? it.evaluations : [],
    __unit: unitOf(it),
  };
}

function rowToPayload(row) {
  const {
    __id, __itemId, __position, __created, __updated, __expected, __expectedRaw, __metadata, __origin, __assigned, __comments, __tags,
    __output, __messages, __evaluations, __unit, __expired, __query, ...input
  } = row;
  let metadata = {};
  const mstr = __metadata != null ? String(__metadata).trim() : '';
  if (mstr) {
    try { const parsed = JSON.parse(mstr); if (parsed && typeof parsed === 'object') metadata = parsed; else metadata = { value: parsed }; }
    catch { metadata = { note: mstr }; }
  }
  const typedInput = {};
  for (const [k, v] of Object.entries(input)) typedInput[k] = coerceVal(v);
  return {
    id: __itemId || undefined,
    input: typedInput,
    ...(__query !== null && __query !== undefined ? { query: __query } : {}),
    expected_output: expectedForSave(__expected, __expectedRaw),
    metadata,
    source_trace_ref: __origin && typeof __origin === 'object' ? __origin : null,
    assigned_to: __assigned != null && __assigned !== '' ? Number(__assigned) : null,
    comments: normalizeComments(__comments),
    tags: normalizeTags(__tags),
  };
}

/**
 * Spreadsheet editor for a dataset. Columns are the prompt's variables; rows are
 * { column: value } maps. Exposes save()/isDirty() via ref so the page can host
 * a single Save button. Calls onDirtyChange so the page reflects unsaved state.
 *
 * A saved dataset loads one page of PAGE_SIZE rows from the server. When the
 * whole working set fits in that page, filters and paging stay in memory (the
 * same result, and the tests' synchronous filter). When it does not, Next,
 * Previous and Filter each ask the server for that page only.
 */
const DatasetGrid = forwardRef(function DatasetGrid({
  dataset, onSaved, onDirtyChange, onOpenOrigin,
  selectedRowId, onRowSelect, onPageItems, onError,
  fillHeight = false, persist, readOnly = false, expiredItemIds = null, onExportSelection = null,
  toolbarLeading = null,
}, ref) {
  // Input columns are the dataset's columns minus any reserved (expected/metadata)
  // names an older flat dataset may have carried.
  const initialCols = useMemo(() => {
    // A column is a name; ones seeded from a prompt's variables arrive as { name, type }.
    const names = (Array.isArray(dataset.columns) ? dataset.columns : [])
      .map((c) => (c && typeof c === 'object' ? c.name : c))
      .filter((c) => typeof c === 'string' && c && !RESERVED.has(c));
    const cols = [...new Set(names)];
    return cols.length ? cols : ['input'];
  }, [dataset.columns]);
  const [columns, setColumns] = useState(initialCols);
  const [derivedColumns, setDerivedColumns] = useState(() =>
    normalizeDerivedColumns(dataset.derived_columns || []),
  );
  const [derivedModal, setDerivedModal] = useState(null); // null | { editing?: def }
  const [filters, setFilters] = useState([]);
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [serverPage, setServerPage] = useState(0);
  // 'full': every active row is in memory (a draft, or one page holds them all).
  // 'page': the server has more than PAGE_SIZE rows; `rows` is the current page.
  // 'unknown': the first request has not come back.
  const [mode, setMode] = useState(() => (dataset.id ? 'unknown' : 'full'));
  const [ready, setReady] = useState(() => !dataset.id);
  const [paging, setPaging] = useState(false);
  const [itemCount, setItemCount] = useState(() => (dataset.id ? 0 : (dataset.items || []).length));
  const [serverTotal, setServerTotal] = useState(0);
  const [virtualTotal, setVirtualTotal] = useState(0);
  const [rows, setRows] = useState(() => (dataset.id ? [] : (dataset.items || []).map((it, i) => itemToRow(it, i))));
  const rowsRef = useRef(rows);
  const modeRef = useRef(dataset.id ? 'unknown' : 'full');
  // The column filters plus the search bar as one more condition: server and in-memory paths read the same list.
  const allFilters = useMemo(
    () => (search.trim() ? [...filters, { id: SEARCH_FIELD, field: SEARCH_FIELD, operator: 'contains', value: search }] : filters),
    [filters, search],
  );
  const filtersRef = useRef(allFilters);
  const columnsRef = useRef(columns);
  const serverItemsRef = useRef([]);
  const serverTotalRef = useRef(0);
  const offsetRef = useRef(0);
  const addedRef = useRef([]);
  const deletedRef = useRef(new Set());
  const deletedRowsRef = useRef([]);
  const patchesRef = useRef(new Map());
  const virtualTotalRef = useRef(0);
  const reqRef = useRef(0);
  const filterKeyRef = useRef('');
  const onPageItemsRef = useRef(onPageItems);
  const onErrorRef = useRef(onError);
  const onRowSelectRef = useRef(onRowSelect);
  const pendingSelectRef = useRef(null);
  const skipPageRef = useRef(null);
  filtersRef.current = allFilters;
  columnsRef.current = columns;
  onPageItemsRef.current = onPageItems;
  onErrorRef.current = onError;
  onRowSelectRef.current = onRowSelect;

  const updateRows = (fn) => {
    const next = typeof fn === 'function' ? fn(rowsRef.current) : fn;
    rowsRef.current = next;
    setRows(next);
    return next;
  };
  // Rows whose source span / run has been swept by retention: the page checks once and passes the ids down.
  const expired = useMemo(() => new Set(expiredItemIds || []), [expiredItemIds]);
  const [dirty, setDirty] = useState(false);
  const [imp, setImp] = useState(null);
  const fileRef = useRef(null);
  const downloadRef = useRef(() => {});
  const { alert: appAlert } = useAppDialog();

  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  const markDirty = () => setDirty(true);

  const activeFilterList = useMemo(() => allFilters.filter(isActive), [allFilters]);
  const filterKey = useMemo(
    () => JSON.stringify(activeFilterList.map((f) => [f.field, f.operator, f.value ?? ''])),
    [activeFilterList],
  );
  const activeFilters = activeFilterList.length;
  const columnFilterCount = useMemo(() => filters.filter(isActive).length, [filters]);

  // The page the server last returned, with unsaved edits laid on top.
  // Adds sit after the server rows and are sliced into the same page size.
  const composeRef = useRef(() => {});
  composeRef.current = () => {
    const deleted = deletedRef.current;
    const patches = patchesRef.current;
    const offset = offsetRef.current;
    const total = serverTotalRef.current;
    const matchingAdds = applyRowFilters(addedRef.current, filtersRef.current, columnsRef.current);
    const matchingDeleted = applyRowFilters(deletedRowsRef.current, filtersRef.current, columnsRef.current);
    const adjusted = Math.max(0, total - matchingDeleted.length) + matchingAdds.length;
    virtualTotalRef.current = adjusted;
    setVirtualTotal(adjusted);
    const mapped = serverItemsRef.current
      .map((it, i) => itemToRow(it, offset + i))
      .filter((r) => !deleted.has(r.__itemId))
      .map((r) => patches.get(r.__itemId) || r);
    const addStart = total;
    const addEnd = total + matchingAdds.length;
    const pageEnd = offset + PAGE_SIZE;
    const overlapStart = Math.max(offset, addStart);
    const overlapEnd = Math.min(pageEnd, addEnd);
    const addSlice = overlapStart < overlapEnd
      ? matchingAdds.slice(overlapStart - addStart, overlapEnd - addStart)
      : [];
    updateRows([...mapped, ...addSlice]);
  };

  useEffect(() => {
    if (!dataset.id) return undefined;
    if (modeRef.current === 'full') return undefined;
    if (skipPageRef.current === serverPage) { skipPageRef.current = null; return undefined; }
    const filterChanged = filterKeyRef.current !== filterKey;
    filterKeyRef.current = filterKey;
    const delay = modeRef.current === 'page' && filterChanged ? 200 : 0;
    const token = ++reqRef.current;
    const timer = setTimeout(async () => {
      setPaging(true);
      try {
        const offset = serverPage * PAGE_SIZE;
        const pageResult = await listDatasetItems(dataset.id, {
          limit: PAGE_SIZE, offset, filters: filtersRef.current,
        });
        if (token !== reqRef.current) return;
        const wholeSet = pageResult.item_count <= PAGE_SIZE && offset === 0 && !filtersRef.current.some(isActive)
          && addedRef.current.length === 0 && deletedRef.current.size === 0;
        if (modeRef.current !== 'page' && wholeSet) {
          modeRef.current = 'full';
          setMode('full');
          setItemCount(pageResult.item_count);
          setServerTotal(pageResult.item_count);
          serverTotalRef.current = pageResult.item_count;
          updateRows(pageResult.items.map((it, i) => itemToRow(it, i)));
          setReady(true);
          onPageItemsRef.current?.(pageResult.items);
          return;
        }
        modeRef.current = 'page';
        setMode('page');
        serverItemsRef.current = pageResult.items;
        serverTotalRef.current = pageResult.total;
        offsetRef.current = pageResult.offset;
        setItemCount(pageResult.item_count);
        setServerTotal(pageResult.total);
        composeRef.current();
        setReady(true);
        onPageItemsRef.current?.(pageResult.items);
        if (pendingSelectRef.current && rowsRef.current.some((r) => r.__id === pendingSelectRef.current)) {
          const openId = pendingSelectRef.current;
          pendingSelectRef.current = null;
          onRowSelectRef.current?.(openId, 'fields');
        }
      } catch (e) {
        if (token === reqRef.current) onErrorRef.current?.(e);
      } finally {
        if (token === reqRef.current) setPaging(false);
      }
    }, delay);
    return () => { reqRef.current += 1; clearTimeout(timer); };
  }, [dataset.id, serverPage, filterKey]);

  const visibleRows = useMemo(
    () => (mode === 'full' ? applyRowFilters(rows, allFilters, columns) : rows),
    [mode, rows, allFilters, columns],
  );
  const shownTotal = mode === 'page' ? virtualTotal : visibleRows.length;
  const pageCount = Math.max(1, Math.ceil((mode === 'page' ? Math.max(serverTotal + addedRef.current.length, shownTotal) : shownTotal) / PAGE_SIZE));
  const safePage = Math.min(mode === 'page' ? serverPage : page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageRows = mode === 'page' ? rows : visibleRows.slice(pageStart, pageStart + PAGE_SIZE);

  const openDerivedModal = useCallback(() => setDerivedModal({}), []);

  /** Add a blank row at the end and open it in the row panel, ready to fill. */
  const addRow = () => {
    const id = newId();
    const row = { __id: id, __position: (modeRef.current === 'page' ? serverTotalRef.current : rowsRef.current.length) + addedRef.current.length, __comments: [], __tags: [] };
    if (modeRef.current === 'page') {
      addedRef.current = [...addedRef.current, row];
      const index = serverTotalRef.current + addedRef.current.length - 1;
      const target = Math.floor(index / PAGE_SIZE);
      const filtersWillChange = filtersRef.current.some(isActive);
      setFilters((f) => f.filter((x) => !isActive(x)));
      setSearch('');
      if (target !== serverPage) setServerPage(target);
      if (!filtersWillChange && target === serverPage) {
        composeRef.current();
        onRowSelect?.(id, 'fields');
      } else pendingSelectRef.current = id;
    } else {
      updateRows((r) => [...r, row]);
      setPage(Math.floor((rowsRef.current.length - 1) / PAGE_SIZE));
      setFilters((f) => f.filter((x) => !isActive(x)));
      setSearch('');
      onRowSelect?.(id, 'fields');
    }
    markDirty();
  };

  const forgetRow = (row) => {
    if (!row) return;
    if (row.__itemId && modeRef.current === 'page') {
      deletedRef.current.add(row.__itemId);
      deletedRowsRef.current = [...deletedRowsRef.current.filter((r) => r.__itemId !== row.__itemId), row];
      patchesRef.current.delete(row.__itemId);
    } else if (!row.__itemId) {
      addedRef.current = addedRef.current.filter((r) => r.__id !== row.__id);
    }
  };

  const deleteChecked = () => {
    const gone = checkedIds;
    const victims = rowsRef.current.filter((r) => gone.has(r.__id));
    victims.forEach(forgetRow);
    updateRows((rs) => rs.filter((r) => !gone.has(r.__id)));
    if (modeRef.current === 'page') composeRef.current();
    if (selectedRowId && gone.has(selectedRowId)) onRowSelect?.(null);
    setCheckedIds(new Set());
    markDirty();
  };

  const allChecked = pageRows.length > 0 && pageRows.every((r) => checkedIds.has(r.__id));

  const gridColumns = useMemo(() => {
    const show = (k) => !hidden.has(k);
    return [
      ...(readOnly ? [] : [{
        key: '__check', name: '', width: 36, frozen: true, resizable: false,
        renderHeaderCell: () => (
          <input type="checkbox" aria-label={allChecked ? 'Clear the selection' : 'Select all rows'} checked={allChecked}
            onChange={(e) => setCheckedIds(e.target.checked ? new Set(pageRows.map((r) => r.__id)) : new Set())}
            onClick={(e) => e.stopPropagation()} />
        ),
        renderCell: ({ row }) => (
          <input type="checkbox" aria-label="Select row" checked={checkedIds.has(row.__id)}
            onChange={(e) => {
              setCheckedIds((prev) => {
                const next = new Set(prev);
                if (e.target.checked) next.add(row.__id); else next.delete(row.__id);
                return next;
              });
            }}
            onClick={(e) => e.stopPropagation()} />
        ),
      }]),
      {
        key: '__n', name: '#', width: 40, frozen: true, resizable: false,
        renderHeaderCell: () => <span className="flex h-full w-full items-center justify-center">#</span>,
        renderCell: (p) => (
          <span
            className="flex h-full cursor-pointer items-center justify-center text-[color:var(--text-disabled)] hover:text-[color:var(--text-primary)]"
            onClick={(e) => { e.stopPropagation(); onRowSelect?.(p.row.__id, 'fields'); }}
            onKeyDown={(e) => { if (e.key === 'Enter') onRowSelect?.(p.row.__id, 'fields'); }}
            role="button"
            tabIndex={0}
          >{pageStart + p.rowIdx + 1}</span>
        ),
      },
      ...(show('__links') ? [{
        key: '__links', name: 'Links', width: 190, resizable: true, minWidth: 110,
        renderHeaderCell: () => <HeaderLabel name="Links" title="What the row is about: open the eval, run, span or conversation" />,
        renderCell: ({ row }) => {
          const links = linksOf(row.__unit);
          if (links.length) return <LinkChips links={links} expired={row.__expired || expired.has(row.__itemId)} onOpen={onOpenOrigin} />;
          // A row typed or imported by hand: where it came from, if anywhere.
          const o = row.__origin;
          if (!o) return <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
          const kind = originKind(o);
          const label = originLabel(o);
          const title = originTitle(o);
          // A row an experiment wrote (retired Nov 2026) keeps its label, with nothing to open.
          if (kind === 'trace') {
            return (
              <span className="flex h-full items-start px-1.5 py-1">
                <button type="button" title={title} onClick={(e) => { e.stopPropagation(); onOpenOrigin?.(o); }} className={linkBtn}>
                  {label || 'Log'}
                </button>
              </span>
            );
          }
          return label ? <span title={title} className="block truncate px-2 text-[11px] text-[color:var(--text-secondary)]">{label}</span> : <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
        },
      }] : []),
      // ONE Input column: the whole transcript as text (role: text per turn), else
      // the variables as key: value. The variables stay the row's input for saving
      // and for prompt binding; they are edited in the drawer. The rows grow to
      // fit (rowHeightFor), so the table shows the row, not a summary of it.
      {
        key: '__input', name: 'Input', resizable: true, width: TEXT_COL_WIDTH.input, minWidth: 160,
        renderHeaderCell: () => <HeaderLabel name="Input" />,
        renderCell: ({ row }) => {
          const vars = {};
          for (const c of columns) if (row[c] != null && row[c] !== '') vars[c] = coerceVal(row[c]);
          const lines = inputLines(row.__messages, vars, row.__query).filter((l) => l.key || l.body);
          if (!lines.length) return <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
          return (
            <span className="ds-text-cell">
              {lines.map((l, i) => (
                <span key={`${l.key || ''}:${i}`} className="ds-text-line">
                  {l.key ? <><span className="ds-text-key">{l.key}</span>{l.body ? ': ' : ':'}</> : null}
                  <span className="ds-text-body">{l.body}</span>
                </span>
              ))}
            </span>
          );
        },
      },
      ...(show('__output') ? [{
        key: '__output', name: 'Output', resizable: true, width: TEXT_COL_WIDTH.output, minWidth: 120,
        renderHeaderCell: () => <HeaderLabel name="Output" title="What it produced" />,
        renderCell: ({ row }) => {
          const text = rowOutputText(row);
          return text ? <span className="ds-text-cell">{text}</span> : <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
        },
      }] : []),
      ...(show('__expected') ? [{
        key: '__expected', name: 'Expected', resizable: true, width: TEXT_COL_WIDTH.expected, minWidth: 120,
        renderHeaderCell: () => <HeaderLabel name="Expected" title="What it should have produced. Training exports use Output where this is empty." />,
        renderCell: ({ row }) => {
          const text = rowExpectedText(row);
          return text ? <span className="ds-text-cell">{text}</span> : <span className="px-2 text-[color:var(--text-disabled)]">-</span>;
        },
      }] : []),
      ...(show('__evaluations') ? [{
        key: '__evaluations', name: 'Evaluations', resizable: true, width: TEXT_COL_WIDTH.evaluations, minWidth: 160,
        renderHeaderCell: () => <HeaderLabel name="Evaluations" title="Verdicts on this row. Click one to open it" />,
        renderCell: ({ row }) => <EvaluationChips evaluations={row.__evaluations} onOpen={onOpenOrigin} />,
      }] : []),
      ...(show('__metadata') ? [{
        key: '__metadata', name: 'Metadata', resizable: true, width: 260, minWidth: 120,
        renderHeaderCell: () => <HeaderLabel name="Metadata" />,
        renderCell: ({ row }) => <MetadataCell text={row.__metadata} onOpen={onOpenOrigin} />,
      }] : []),
      ...(show('__created') ? [{
        key: '__created', name: 'Created', width: 148, resizable: true,
        renderHeaderCell: () => <HeaderLabel name="Created" />,
        renderCell: ({ row }) => (
          <span className="ds-cell-json text-[11px] text-[color:var(--text-muted)]">{formatCreated(row.__created)}</span>
        ),
      }] : []),
      ...(show('__updated') ? [{
        key: '__updated', name: 'Updated', width: 148, resizable: true,
        renderHeaderCell: () => <HeaderLabel name="Updated" />,
        renderCell: ({ row }) => (
          <span className="ds-cell-json text-[11px] text-[color:var(--text-muted)]">{row.__updated ? formatCreated(row.__updated) : '—'}</span>
        ),
      }] : []),
      ...derivedColumns.map((def) => ({
        key: `__derived_${def.id}`,
        name: def.name,
        resizable: true,
        width: 130,
        minWidth: 70,
        cellClass: 'ds-derived-cell',
        renderHeaderCell: () => (
          <HeaderLabel name={def.name} title={readOnly ? 'Derived column' : 'Derived column. Click to edit'}
            onClick={readOnly ? undefined : () => setDerivedModal({ editing: def })} />
        ),
        renderCell: ({ row }) => {
          const val = evaluateDerivedColumn(def, row, columns);
          return (
            <span className="block truncate px-1 text-[12px] text-[color:var(--text-secondary)]" title={val || undefined}>
              {val || <span className="text-[color:var(--text-disabled)]">-</span>}
            </span>
          );
        },
      })),
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, derivedColumns, hidden, checkedIds, pageRows, pageStart, allChecked, readOnly, onOpenOrigin, onRowSelect, expired]);

  const onCellClick = useCallback((args) => {
    onRowSelect?.(args.row.__id, 'fields');
  }, [onRowSelect]);

  const patchRow = useCallback((rowId, patch) => {
    updateRows((rs) => rs.map((r) => {
      if (r.__id !== rowId) return r;
      const next = { ...r };
      if (patch.input) {
        const keys = Object.keys(patch.input).filter((k) => !RESERVED.has(k));
        setColumns((cols) => {
          const nc = [...cols];
          keys.forEach((k) => { if (!nc.includes(k)) nc.push(k); });
          return nc;
        });
        for (const k of Object.keys(next)) {
          if (!k.startsWith('__') && !keys.includes(k)) delete next[k];
        }
        keys.forEach((k) => { next[k] = displayVal(patch.input[k]); });
      }
      if (patch.query !== undefined) next.__query = patch.query;
      if (patch.expected !== undefined) next.__expected = patch.expected == null ? '' : (typeof patch.expected === 'string' ? patch.expected : JSON.stringify(patch.expected));
      if (patch.metadata !== undefined) next.__metadata = metaToStr(patch.metadata);
      if (patch.assignedTo !== undefined) {
        next.__assigned = patch.assignedTo != null && patch.assignedTo !== '' ? Number(patch.assignedTo) : null;
      }
      if (patch.comments !== undefined) next.__comments = normalizeComments(patch.comments);
      if (patch.tags !== undefined) next.__tags = normalizeTags(patch.tags);
      if (next.__itemId && modeRef.current === 'page') patchesRef.current.set(next.__itemId, next);
      else addedRef.current = addedRef.current.map((a) => (a.__id === next.__id ? next : a));
      return next;
    }));
    markDirty();
  }, []);

  const contextFor = useCallback((row, globalIndex, count) => ({
    rowIndex: globalIndex,
    rowCount: count,
    row: { ...buildRowSnapshot(row, columnsRef.current), expired: Boolean(row?.__expired || expired.has(row?.__itemId)) },
    inputColumns: columnsRef.current,
    rowId: row.__id,
  }), [expired]);

  const getRowDrawerContext = useCallback((rowId) => {
    const list = rowsRef.current;
    const idx = list.findIndex((r) => r.__id === rowId);
    if (idx < 0) return null;
    const globalIndex = modeRef.current === 'page' ? offsetRef.current + idx : idx;
    const count = modeRef.current === 'page' ? virtualTotalRef.current : list.length;
    return contextFor(list[idx], globalIndex, count);
  }, [contextFor]);

  const getRowDrawerContextByIndex = useCallback((index) => {
    if (modeRef.current === 'page') {
      const local = index - offsetRef.current;
      const list = rowsRef.current;
      if (local < 0 || local >= list.length) return null;
      return contextFor(list[local], index, virtualTotalRef.current);
    }
    const list = rowsRef.current;
    if (index < 0 || index >= list.length) return null;
    return contextFor(list[index], index, list.length);
  }, [contextFor]);

  const saveDerivedColumn = (def) => {
    setDerivedColumns((cols) => {
      const idx = cols.findIndex((c) => c.id === def.id);
      if (idx >= 0) {
        const next = [...cols];
        next[idx] = def;
        return next;
      }
      return [...cols, def];
    });
    markDirty();
    setDerivedModal(null);
  };

  const deleteDerivedColumn = (id) => {
    setDerivedColumns((cols) => cols.filter((c) => c.id !== id));
    markDirty();
  };

  const reservedColumnNames = useMemo(
    () => [...columns, ...derivedColumns.map((d) => d.name)],
    [columns, derivedColumns],
  );

  const handleSave = async () => {
    // A paged dataset is not all in memory. Send the rows that changed, the
    // rows that were added, and the ids that were removed. A full set (one
    // page holds every row, or a draft) still sends the whole working set.
    let saved = null;
    if (modeRef.current === 'page' && dataset.id && !persist) {
      const changed = [...patchesRef.current.values()];
      const created = addedRef.current;
      const seen = new Set();
      const payloadRows = [...changed, ...created].filter((r) => {
        const key = r.__itemId || r.__id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map((r) => rowToPayload(r));
      saved = await replaceRows(dataset.id, columns, payloadRows, derivedColumns, {
        partial: true, archiveIds: [...deletedRef.current],
      });
    } else {
      // Only the fields the grid owns are sent. The server merges, so Output,
      // Evaluations, messages and the label columns are untouched by a save.
      const payloadRows = rowsRef.current.map((r) => rowToPayload(r));
      if (persist) await persist(columns, payloadRows, derivedColumns);
      else saved = await replaceRows(dataset.id, columns, payloadRows, derivedColumns);
    }
    setDirty(false);
    if (onSaved) onSaved(saved);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({
    save: handleSave,
    isDirty: () => dirty,
    getRowDrawerContext,
    getRowDrawerContextByIndex,
    patchRow,
    openDerivedModal,
    openImport: () => fileRef.current?.click(),
    addRow,
    download: () => downloadRef.current(),
    deleteRow: (rowId) => {
      const row = rowsRef.current.find((r) => r.__id === rowId);
      forgetRow(row);
      updateRows((rs) => rs.filter((r) => r.__id !== rowId));
      if (modeRef.current === 'page') composeRef.current();
      markDirty();
    },
    // Evaluation snapshots come from the server, never from the grid, so
    // replacing them is not an edit: the page is not marked unsaved.
    // The ref is updated before setState so the open drawer can read it
    // in the same turn.
    setEvaluations: (byItemId) => {
      updateRows((rs) => rs.map((r) => (r.__itemId && byItemId[r.__itemId] ? { ...r, __evaluations: byItemId[r.__itemId] } : r)));
    },
    patchEvaluation: (itemId, evalRunId, feedback) => {
      updateRows((rs) => rs.map((r) => (r.__itemId !== itemId ? r : {
        ...r,
        __evaluations: (r.__evaluations || []).map((ev) => (ev.eval_run_id === evalRunId ? { ...ev, feedback } : ev)),
      })));
    },
    reload: async () => {
      if (!dataset.id || modeRef.current === 'full') return;
      const token = ++reqRef.current;
      const pageResult = await listDatasetItems(dataset.id, {
        limit: PAGE_SIZE, offset: serverPage * PAGE_SIZE, filters: filtersRef.current,
      });
      if (token !== reqRef.current) return;
      serverItemsRef.current = pageResult.items;
      serverTotalRef.current = pageResult.total;
      offsetRef.current = pageResult.offset;
      setItemCount(pageResult.item_count);
      setServerTotal(pageResult.total);
      composeRef.current();
      onPageItemsRef.current?.(pageResult.items);
    },
    openGlobalIndex: async (index) => {
      if (modeRef.current !== 'page' || !dataset.id) return null;
      if (index < 0 || index >= Math.max(virtualTotalRef.current, serverTotalRef.current + addedRef.current.length)) return null;
      const target = Math.floor(index / PAGE_SIZE);
      if (target !== serverPage) {
        skipPageRef.current = target;
        setServerPage(target);
      } else if (offsetRef.current === target * PAGE_SIZE) {
        return rowsRef.current[index - offsetRef.current]?.__id || null;
      }
      const token = ++reqRef.current;
      const pageResult = await listDatasetItems(dataset.id, {
        limit: PAGE_SIZE, offset: target * PAGE_SIZE, filters: filtersRef.current,
      });
      if (token !== reqRef.current) return null;
      serverItemsRef.current = pageResult.items;
      serverTotalRef.current = pageResult.total;
      offsetRef.current = pageResult.offset;
      setItemCount(pageResult.item_count);
      setServerTotal(pageResult.total);
      composeRef.current();
      onPageItemsRef.current?.(pageResult.items);
      return rowsRef.current[index - pageResult.offset]?.__id || null;
    },
  }), [dirty, rows, columns, derivedColumns, getRowDrawerContext, getRowDrawerContextByIndex, patchRow, openDerivedModal, persist, dataset.id, serverPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const table = await parseImportFile(file);
      setImp({ ...table, filename: file.name });
    } catch (err) {
      appAlert(err instanceof ImportProblem ? err.message : `Could not read the file: ${err.message}`, { title: 'Import failed', variant: 'error' });
    }
  };

  const applyImport = (mapping) => {
    const out = buildImportedRows({ ...imp, mapping, columns, newId });
    setColumns(out.columns);
    if (modeRef.current === 'page') {
      addedRef.current = [...addedRef.current, ...out.rows];
      const index = serverTotalRef.current + addedRef.current.length - out.rows.length;
      setServerPage(Math.max(0, Math.floor(index / PAGE_SIZE)));
      composeRef.current();
    } else {
      updateRows((r) => [...r, ...out.rows]);
    }
    setImp(null);
    markDirty();
  };

  const rowsForExport = async () => {
    if (modeRef.current !== 'page' || !dataset.id) return visibleRows;
    const all = [];
    let offset = 0;
    let total = Infinity;
    const exportFilters = filtersRef.current;
    while (offset < total && all.length < 100000) {
      // eslint-disable-next-line no-await-in-loop
      const pageResult = await listDatasetItems(dataset.id, { limit: 200, offset, filters: exportFilters });
      total = pageResult.total;
      all.push(...pageResult.items.map((it, i) => itemToRow(it, offset + i)));
      if (!pageResult.items.length) break;
      offset += pageResult.items.length;
    }
    const deleted = deletedRef.current;
    const patches = patchesRef.current;
    const saved = all.filter((r) => !deleted.has(r.__itemId)).map((r) => patches.get(r.__itemId) || r);
    const adds = applyRowFilters(addedRef.current, exportFilters, columns);
    return [...saved, ...adds];
  };

  const exportCsv = () => {
    const header = [...columns, 'output', 'expected', 'metadata'];
    const write = (source) => {
    const flat = source.map((row) => ({
      ...Object.fromEntries(columns.map((c) => [c, row[c]])),
      output: rowOutputText(row), expected: row.__expected, metadata: row.__metadata,
    }));
    const blob = new Blob([toCsv(header, flat)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${String(dataset.name || 'dataset').replace(/[^\w.-]+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    };
    if (modeRef.current === 'page' && dataset.id) {
      rowsForExport().then(write).catch((e) => onErrorRef.current?.(e));
      return;
    }
    write(visibleRows);
  };
  downloadRef.current = exportCsv;

  const empty = ready && rows.length === 0 && itemCount === 0 && addedRef.current.length === 0;
  // The empty state sits over the whole column width, which can be wider than
  // the viewport; pin it to the visible part of the grid instead.
  const gridBox = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);
  useEffect(() => {
    const el = gridBox.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const grid = el.querySelector('.rdg');
      const next = (grid || el).clientWidth;
      setGridWidth((w) => (Math.abs(w - next) < 2 ? w : next));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);
  // When the visible columns are narrower than the grid, the last one takes
  // the leftover width so the row reaches the edge of the viewport.
  const fittedColumns = useMemo(() => {
    if (!gridWidth || gridColumns.length === 0) return gridColumns;
    const last = gridColumns[gridColumns.length - 1];
    const preferred = Number(last.width) || Number(last.minWidth) || 120;
    const used = gridColumns.slice(0, -1).reduce((n, c) => n + (Number(c.width) || Number(c.minWidth) || 0), 0);
    const room = gridWidth - used;
    if (room <= preferred) return gridColumns;
    return [...gridColumns.slice(0, -1), { ...last, width: room }];
  }, [gridColumns, gridWidth]);
  return (
    <div className={`ds-workspace relative flex flex-col ${fillHeight ? 'min-h-0 flex-1' : 'gap-2'}`}>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-3 py-2">
        {toolbarLeading}
        <button type="button" onClick={() => { if (!filters.length) setFilters([newFilter()]); setFilterOpen((v) => !v); }} aria-expanded={filterOpen} className={activityToolbarButtonClass}>
          <FilterIcon size={14} color="var(--text-muted)" />
          Filter
          {columnFilterCount > 0 && (
            <span className="rounded-full bg-[color:var(--text-primary)] px-1.5 text-[10px] font-semibold text-ink-onBrand">{columnFilterCount}</span>
          )}
        </button>
        <div className="relative w-56 max-w-full">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
          <input
            type="search" value={search} aria-label="Search all columns" placeholder="Search all columns" maxLength={200}
            onChange={(e) => {
              setSearch(e.target.value);
              if (modeRef.current === 'page') setServerPage(0); else setPage(0);
            }}
            className="h-8 w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] pl-7 pr-7 text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)] [&::-webkit-search-cancel-button]:hidden"
          />
          {search ? (
            <button type="button" aria-label="Clear search" onClick={() => { setSearch(''); if (modeRef.current === 'page') setServerPage(0); else setPage(0); }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><XIcon size={12} /></button>
          ) : null}
        </div>
        <div className="flex-1" />
        <ColumnsMenu hidden={hidden} onToggle={(k) => setHidden((h) => { const n = new Set(h); if (n.has(k)) n.delete(k); else n.add(k); return n; })} />
        <input ref={fileRef} type="file" accept=".csv,.tsv,.xlsx,.xls,.json,.jsonl,.ndjson" className="hidden" onChange={onPickFile} aria-label="Import file" />
      </div>

      {filterOpen ? (
        <DatasetFilter filters={filters} columns={columns}
          onChange={(next) => {
            setFilters(next);
            if (!next.length) setFilterOpen(false);
            if (modeRef.current === 'page') setServerPage(0);
          }} />
      ) : null}
      {activeFilters > 0 ? (
        <div className="shrink-0 border-b border-[color:var(--border-subtle)] px-3 pb-1.5 text-[11px] text-[color:var(--text-muted)]">
          {(mode === 'page' ? virtualTotal : visibleRows.length).toLocaleString()} of {(mode === 'page' ? itemCount : rows.length).toLocaleString()} rows
        </div>
      ) : null}

      <div ref={gridBox} className={`relative overflow-hidden ${fillHeight ? 'min-h-0 flex-1' : 'rounded-md border border-[color:var(--border-default)]'}`} style={fillHeight ? { height: '100%' } : { height: Math.min(460, 88 + visibleRows.length * 32) }}>
        <DataGrid
          columns={fittedColumns}
          rows={pageRows}
          onCellClick={onCellClick}
          rowKeyGetter={(r) => r.__id}
          rowClass={(row) => (row.__id === selectedRowId ? 'ds-row-selected' : undefined)}
          className="trodo-dsgrid"
          style={gridThemeVars}
          rowHeight={(row) => rowHeightFor(row, columns, { output: !hidden.has('__output'), expected: !hidden.has('__expected'), evaluations: !hidden.has('__evaluations') })}
          headerRowHeight={HEADER_ROW_HEIGHT}
          renderers={{
            noRowsFallback: !ready || empty ? null : (
              <div className="col-span-full flex flex-col items-center justify-center gap-3 py-10 text-center" style={{ position: 'sticky', left: 0, width: gridWidth ? `${gridWidth}px` : '100%' }}>
                <p className="text-[12px] text-[color:var(--text-muted)]">No rows match.</p>
              </div>
            ),
          }}
        />
        {empty ? (
          <div className="ds-dataset-empty" style={{ top: HEADER_ROW_HEIGHT }}>
            <div className="ds-dataset-empty-inner">
              <DatasetEmptyArt />
              <p className="ds-dataset-empty-title">Your dataset is empty</p>
              <p className="ds-dataset-empty-desc">Add a row, import a file, or connect a source to get started.</p>
            </div>
          </div>
        ) : null}
      </div>

      {ready && (mode === 'page' ? virtualTotal : visibleRows.length) > 0 ? (
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[color:var(--border-default)] px-3 py-1.5 text-[12px] text-[color:var(--text-muted)]">
          <span className="tabular-nums">{(pageStart + 1).toLocaleString()}–{(pageStart + pageRows.length).toLocaleString()} of {(mode === 'page' ? virtualTotal : visibleRows.length).toLocaleString()}</span>
          <button type="button" onClick={() => (mode === 'page' ? setServerPage(safePage - 1) : setPage(safePage - 1))} disabled={paging || safePage === 0} className={`${activityToolbarButtonClass} disabled:opacity-50`}>Previous</button>
          <button type="button" onClick={() => (mode === 'page' ? setServerPage(safePage + 1) : setPage(safePage + 1))} disabled={paging || safePage >= pageCount - 1} className={`${activityToolbarButtonClass} disabled:opacity-50`}>Next</button>
        </div>
      ) : null}

      {imp ? <ImportDialog table={imp} columns={columns} onApply={applyImport} onClose={() => setImp(null)} /> : null}
      {derivedModal ? (
        <DerivedColumnModal
          key={derivedModal.editing?.id || 'new'}
          onClose={() => setDerivedModal(null)}
          onSave={saveDerivedColumn}
          onDelete={deleteDerivedColumn}
          existing={derivedModal.editing}
          rows={rows}
          inputColumns={columns}
          reservedNames={reservedColumnNames.filter((n) => n !== derivedModal.editing?.name)}
        />
      ) : null}
      <BulkBar count={readOnly ? 0 : checkedIds.size} label="Selected rows" onClear={() => setCheckedIds(new Set())}
        actions={(() => {
          // Only saved rows have an id the server knows; export needs one.
          const saved = rows.filter((r) => checkedIds.has(r.__id) && r.__itemId);
          const savedIds = saved.map((r) => r.__itemId);
          return [
            ...(onExportSelection ? [{ label: 'Export', disabled: !savedIds.length, onClick: () => onExportSelection(savedIds) }] : []),
            { label: 'Archive', onClick: deleteChecked, danger: true, title: 'Remove from the working set when you Save. Archived rows stay in past versions.' },
          ];
        })()} />
    </div>
  );
});

export default DatasetGrid;
